import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, MenuItem, Stack, TextField, Typography
} from '@mui/material';
import { CardGiftcard as GrantIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import PremiumModal from 'components/shared/modals/PremiumModal';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import {
  ORGANIZATION_MEMBERS, ORGANIZATION, GRANT_ORGANIZATION_MEMBERSHIPS
} from 'api/graphql/organization';

/**
 * Otorga membresías pagadas por la empresa (100%) a los miembros seleccionados: crea un
 * ClientPayment por usuario y el backend activa el plan. Renovación = re-otorgar por periodo.
 */
function OrganizationGrantModal({ open, organizationId, onClose, onGranted = null }) {
  const { t } = useTranslation(['organizations', 'buttons']);
  const [planIds, setPlanIds] = useState([]);
  const [months, setMonths] = useState(1);
  const [selected, setSelected] = useState({});
  const [result, setResult] = useState(null);

  const { data: membersData, loading: loadingMembers } = useQuery(ORGANIZATION_MEMBERS, {
    variables: { organization_id: [Number(organizationId)], first: 300, page: 1 },
    fetchPolicy: 'cache-and-network'
  });
  const { data: orgData } = useQuery(ORGANIZATION, { variables: { id: organizationId }, fetchPolicy: 'cache-and-network' });
  const [grantMemberships, { loading: granting }] = useMutation(GRANT_ORGANIZATION_MEMBERSHIPS);

  const members = membersData?.users?.data ?? [];
  // Otorgar = la empresa paga el 100%: solo mostramos los planes ACTIVOS cubiertos al 100%.
  // Los parciales (ej. 50%) NO se otorgan gratis; el miembro paga su parte con descuento en checkout.
  const plans = useMemo(() => (orgData?.organization?.plan_discounts ?? [])
    .filter((p) => p.is_active && p.client_discount_percent >= 100)
    .map((p) => ({ id: String(p.plan_id), name: p.plan_name, coverage: p.client_discount_percent })),
  [orgData]);

  // Precarga: si la org solo vende un plan, queda seleccionado por defecto.
  useEffect(() => {
    if (plans.length === 1) {
      setPlanIds((prev) => (prev.length === 0 ? [plans[0].id] : prev));
    }
  }, [plans]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allChecked = members.length > 0 && selectedIds.length === members.length;

  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleAll = () => {
    if (allChecked) { setSelected({}); return; }
    const next = {};
    members.forEach((m) => { next[m.id] = true; });
    setSelected(next);
  };

  const handleGrant = async () => {
    if (planIds.length === 0 || selectedIds.length === 0) return;
    const res = await grantMemberships({
      variables: {
        organization_id: organizationId,
        plan_ids: planIds,
        user_ids: selectedIds,
        period_months: Number(months)
      }
    });
    setResult(res?.data?.grantOrganizationMemberships || null);
    if (onGranted) onGranted();
  };

  const actions = result ? (
    <Button onClick={onClose} sx={{ ...premiumButtonSx, px: 3 }}>
      {t('done', { ns: 'buttons', defaultValue: 'Listo' })}
    </Button>
  ) : (
    <>
      <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}>
        {t('cancel', { ns: 'buttons', defaultValue: 'Cancelar' })}
      </Button>
      <Button onClick={handleGrant} disabled={granting || planIds.length === 0 || selectedIds.length === 0} sx={{ ...premiumButtonSx, px: 3 }}>
        {t('grant', { defaultValue: 'Otorgar' })} {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
      </Button>
    </>
  );

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      icon={<GrantIcon />}
      title={t('grantMemberships', { defaultValue: 'Otorgar membresías' })}
      actions={actions}
    >
      {result ? (
        <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', py: 2 }}>
          <CheckIcon sx={{ fontSize: 56, color: '#1B934B' }} />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {t('grantedCount', { defaultValue: '{{count}} membresías otorgadas', count: result.granted })}
          </Typography>
          {result.skipped > 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('skipped', { defaultValue: 'Omitidas' })}: {result.skipped}
            </Typography>
          )}
        </Stack>
      ) : (
        <Stack spacing={2}>
          {plans.length === 0 ? (
            <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.overlay' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('noFullyCoveredPlans', { defaultValue: 'Esta organización no tiene planes cubiertos al 100%. Otorgar es solo para cobertura total; en cobertura parcial el miembro paga su parte con el descuento en el checkout.' })}
              </Typography>
            </Box>
          ) : (
            <TextField
              select
              label={t('plans', { defaultValue: 'Planes' })}
              value={planIds}
              onChange={(e) => setPlanIds(
                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
              )}
              helperText={t('grantFullyCoveredHelp', { defaultValue: 'Solo planes cubiertos al 100% por la empresa. Puedes otorgar varios a la vez.' })}
              SelectProps={{
                multiple: true,
                renderValue: (vals) => (
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {vals.map((val) => {
                      const p = plans.find((pl) => String(pl.id) === String(val));
                      return <Chip key={val} size="small" label={p?.name || val} />;
                    })}
                  </Stack>
                )
              }}
              fullWidth
            >
              {plans.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  <Checkbox checked={planIds.some((id) => String(id) === String(p.id))} sx={{ py: 0, mr: 1 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>{p.name}</Box>
                  <Chip
                    size="small"
                    label={p.coverage >= 100 ? t('coversFull', { defaultValue: 'Cubre 100%' }) : t('coversPct', { defaultValue: 'Cubre {{pct}}%', pct: p.coverage })}
                    sx={{ ml: 1, backgroundColor: 'gold.soft', color: 'var(--otx-gold-text)', fontWeight: 700 }}
                  />
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            type="number"
            label={t('periodMonths', { defaultValue: 'Periodo (meses)' })}
            value={months}
            onChange={(e) => setMonths(e.target.value)}
            inputProps={{ min: 1, max: 24 }}
            fullWidth
          />

          <Box>
            <FormControlLabel
              control={<Checkbox checked={allChecked} indeterminate={selectedIds.length > 0 && !allChecked} onChange={toggleAll} />}
              label={t('selectAll', { defaultValue: 'Seleccionar todos' })}
            />
            <Box sx={{ maxHeight: 240, overflowY: 'auto', border: '1px solid', borderColor: 'hairline.main', borderRadius: 2 }}>
              {loadingMembers && members.length === 0 && (
                <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={20} sx={{ color: 'gold.main' }} /></Box>
              )}
              {members.map((m) => (
                <Stack key={m.id} direction="row" alignItems="center" sx={{ px: 1, borderBottom: (th) => `1px solid ${th.palette.hairline.main}`, '&:last-of-type': { borderBottom: 0 } }}>
                  <Checkbox checked={Boolean(selected[m.id])} onChange={() => toggle(m.id)} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{m.full_name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>{m.email}</Typography>
                  </Box>
                </Stack>
              ))}
              {!loadingMembers && members.length === 0 && (
                <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>{t('noMembers', { defaultValue: 'Sin miembros.' })}</Box>
              )}
            </Box>
          </Box>
        </Stack>
      )}
    </PremiumModal>
  );
}

OrganizationGrantModal.propTypes = {
  open: PropTypes.bool.isRequired,
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose: PropTypes.func.isRequired,
  onGranted: PropTypes.func
};

export default OrganizationGrantModal;
